import { useEffect, useState } from "react";
import { api, formatCOP } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Panel de cupos por devolución de la empresa (mar/2026).
 *
 * Se muestra en el detalle del pedido cuando el cliente tiene cupos activos.
 * Permite aplicar el saldo del cupo al pedido con un clic. El cupo aparece
 * como "Cupo por devolución de la empresa" para que el vendedor lo entienda.
 *
 * Cuando todos los cupos del cliente están usados, este panel se oculta.
 *
 * Props:
 *   - clienteId
 *   - pedido: {id, saldo_pendiente, total, total_pagado, cupos_aplicados_total}
 *   - onAplicado(): callback para refrescar el pedido tras aplicar
 */
export default function CuposDisponiblesPanel({ clienteId, pedido, onAplicado }) {
  const [cupos, setCupos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);
    api.get(`/pedidos/cupos-cliente/${clienteId}`)
      .then(({ data }) => { setCupos(data.cupos || []); setTotal(data.total_disponible || 0); })
      .catch(() => { setCupos([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [clienteId, pedido?.id, pedido?.saldo_pendiente]);

  const aplicarMax = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post(`/pedidos/${pedido.id}/aplicar-cupo`, {});
      toast.success(
        data.esta_pagado
          ? `Cupo aplicado ${formatCOP(data.total_aplicado)}. Pedido queda pagado.`
          : `Cupo aplicado ${formatCOP(data.total_aplicado)}. Saldo pendiente: ${formatCOP(data.nuevo_saldo)}.`
      );
      onAplicado?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar el cupo");
    } finally {
      setAplicando(false);
    }
  };

  // Ocultar cuando no hay cupos activos (ya se usaron todos)
  if (loading || !cupos.length || total <= 0) return null;

  const saldo = Number(pedido?.saldo_pendiente ?? pedido?.total ?? 0);
  const aplicable = Math.min(total, saldo);

  return (
    <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 space-y-2" data-testid="cupos-disponibles-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-emerald-900">
          <Gift className="w-4 h-4" />
          <div className="text-sm font-semibold">
            Cupo por devolución disponible:{" "}
            <span className="tabular-nums">{formatCOP(total)}</span>
          </div>
        </div>
        {saldo > 0 && (
          <Button
            size="sm"
            onClick={aplicarMax}
            disabled={aplicando}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
            data-testid="aplicar-cupo-btn"
          >
            {aplicando ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Gift className="w-3 h-3 mr-2" />}
            Aplicar {formatCOP(aplicable)} al saldo
          </Button>
        )}
      </div>
      <div className="text-[11px] text-emerald-800 space-y-0.5">
        {cupos.slice(0, 3).map((c) => (
          <div key={c.id} className="flex items-center justify-between">
            <span>
              Devolución del pedido{" "}
              <span className="font-mono font-semibold">{c.origen_pedido_numero || "—"}</span>
            </span>
            <span className="tabular-nums font-semibold">{formatCOP(c.monto_disponible)}</span>
          </div>
        ))}
        {cupos.length > 3 && (
          <div className="italic">+ {cupos.length - 3} cupos más</div>
        )}
      </div>
    </div>
  );
}
